// Pure normalization of an Axios/FastAPI error into a string an end user can read.
//
// Split out of lib/api.js on purpose: this file has NO axios dependency (api.js's
// `axios.create` + interceptors are the one thing that makes api.js impossible to
// import for real under Jest — every "@/lib/api" import in this codebase's tests is a
// stub, never the real module, precisely because of that ESM-only dependency). Keeping
// the pure error-shape logic here, with api.js re-exporting it, is what lets it be unit
// tested directly and is exactly the separation lib/checkoutErrors.js already relies on
// (a lib module that takes `t` as an argument, never a hook, so it stays callable from
// non-component code and from a plain Jest test).
//
// Normalise une erreur Axios en CHAÎNE lisible pour l'UI.
// Important : sur un 422, FastAPI renvoie `detail` = TABLEAU d'objets
// {type, loc, msg, ...}. L'affecter tel quel à un state rendu comme enfant
// React fait planter le rendu (React #31). On aplatit toujours en string.
// Backend refusals that reach the UI as a BARE MACHINE CONSTANT in `detail` and have no
// owner downstream: whoever displays the string is the last chance to say something a
// human can act on. Each entry pairs the i18n key with its BASE_LANG (English) text, used
// verbatim when the caller passes no `t` — exactly the arrangement the 422 branch below
// already uses for errors.invalidFieldValue.
//
// Deliberately minimal. A code that a caller ALREADY maps locally (the ERROR_CODE_KEYS
// table in admin/booking/calendarUtils.js, checkoutErrorMessage() on the money path) must
// stay out of here: those handlers intercept before extractApiError is reached, and
// duplicating a code would only make the wording drift between two screens.
//
// The suspension text stays reason-agnostic on purpose. This message can be produced by a
// non-payment suspension AND by a platform-side moderation suspension; naming billing as
// the cause here would be a guess. The console's own banner (admin/AdminLayout.jsx) is
// where the reason is known and the recovery link belongs.
/**
 * The one refusal code the platform answers when a practice is not being served —
 * suspended (either kind), `pending` or `cancelled`. Emitted as a BARE STRING `detail` by
 * tenant_middleware.py (host traffic) and by saas_routes.get_tenant_public (the
 * `/t/{slug}` bootstrap), and as `{code}` by the structured refusals elsewhere.
 */
export const TENANT_SUSPENDED_CODE = "TENANT_SUSPENDED";

/**
 * The two refusals of the owner-binding rule (backend/owner_tenant.py). The console is
 * bound to ONE practice: the one whose address the request arrived on, or the one
 * `X-Tenant-Slug` names, or the single practice the signed-in owner has.
 *
 * `OWNER_TENANT_MISMATCH` — the designated practice is not this account's. On the
 * bootstrap call it usually means the remembered slug is STALE (another account signed
 * in on this browser, or the practice was renamed), which admin/ProtectedRoute.jsx
 * heals by dropping the cache and asking again.
 * `TENANT_SELECTION_REQUIRED` — the owner has several practices and named none; the
 * refusal carries `tenants: [{tenant_id, slug, name}]` so the client can offer the
 * choice instead of guessing one.
 */
export const OWNER_TENANT_MISMATCH_CODE = "OWNER_TENANT_MISMATCH";
export const TENANT_SELECTION_REQUIRED_CODE = "TENANT_SELECTION_REQUIRED";

/**
 * The three refusals of the plan-entitlement gates
 * (docs/architecture/plan-entitlements-contract.md, "Wire codes"), all structured:
 *   403 {code: "CAPABILITY_NOT_IN_PLAN", capability, plan_id}
 *   503 {code: "CAPABILITY_NOT_BUILT",   capability, eta: str | null}
 *   409 {code: "PLAN_LIMIT_REACHED",     capability, limit: int}
 * The capability KEY is a machine identifier and is never shown; the numbers that help
 * the reader act (the ETA, the ceiling) are interpolated through `vars`.
 */
/**
 * 409 {code: "PRACTICE_DELETION_PENDING"}: every checkout of a practice whose deletion is
 * unfinished is refused (D7 contract 3.8), on the signup, billing and promotion screens alike.
 */
export const PRACTICE_DELETION_PENDING_CODE = "PRACTICE_DELETION_PENDING";

/**
 * 409 {code: "ADDRESS_RETIRED"}: the address belonged to a deleted practice and is never held
 * again (D7 contract 3.13). The signup, its live address check and every rename read it.
 */
export const ADDRESS_RETIRED_CODE = "ADDRESS_RETIRED";

/**
 * 409 {code: "ADDRESS_DELETION_PENDING"}: a practice whose deletion is unfinished keeps its
 * address while it is being retired (D7 contract 3.13); the practitioner's and the operator's
 * renames read it.
 */
export const ADDRESS_DELETION_PENDING_CODE = "ADDRESS_DELETION_PENDING";

/**
 * 409 {code: "CONTENT_KEY_RETIRED"}: POST /api/admin/content refused a site-content key that
 * nothing reads any more (the old headline lines, the builder's former keys) — the page
 * that sent it predates the change and must be reloaded (backend migration.py
 * RETIRED_CONTENT_KEYS).
 */
export const CONTENT_KEY_RETIRED_CODE = "CONTENT_KEY_RETIRED";

export const CAPABILITY_NOT_IN_PLAN_CODE = "CAPABILITY_NOT_IN_PLAN";
export const CAPABILITY_NOT_BUILT_CODE = "CAPABILITY_NOT_BUILT";
export const PLAN_LIMIT_REACHED_CODE = "PLAN_LIMIT_REACHED";

/**
 * Code → { key, en, vars?, bare? }.
 *   `vars(detail)` extracts interpolation values from a structured detail, and returns
 *   `null` when the detail lacks one the sentence needs;
 *   `bare` is then the sentence used instead ({key, en}, no placeholders), so a message
 *   is never rendered with an empty "{eta}" or "{limit}" in it.
 */
const KNOWN_CODE_MESSAGES = {
  [TENANT_SUSPENDED_CODE]: {
    key: "errors.tenantSuspended",
    en: "This practice is suspended — changes cannot be saved until it is reactivated.",
  },
  [OWNER_TENANT_MISMATCH_CODE]: {
    key: "errors.ownerTenantMismatch",
    en: "This account does not own the practice this page is acting for.",
  },
  [TENANT_SELECTION_REQUIRED_CODE]: {
    key: "errors.tenantSelectionRequired",
    en: "You own several practices — choose the one to work on.",
  },
  [PRACTICE_DELETION_PENDING_CODE]: {
    key: "errors.practiceDeletionPending",
    en: "This practice is being deleted: no payment can be opened for it.",
  },
  [ADDRESS_RETIRED_CODE]: {
    key: "errors.addressRetired",
    en: "This address belonged to a practice that was deleted, and it can never be used again. Please choose another.",
  },
  [CONTENT_KEY_RETIRED_CODE]: {
    key: "errors.contentKeyRetired",
    en: "This page is out of date: this text is no longer used by your site. Reload the page and edit it again.",
  },
  [ADDRESS_DELETION_PENDING_CODE]: {
    key: "errors.addressDeletionPending",
    en: "This practice is being deleted: its address cannot change.",
  },
  [CAPABILITY_NOT_IN_PLAN_CODE]: {
    key: "errors.capabilityNotInPlan",
    en: "This feature is not included in your plan. You can change your plan from Subscription.",
  },
  [CAPABILITY_NOT_BUILT_CODE]: {
    key: "errors.capabilityNotBuiltEta",
    en: "This feature is coming soon (expected {eta}).",
    vars: (d) => (typeof d?.eta === "string" && d.eta.trim() ? { eta: d.eta } : null),
    bare: { key: "errors.capabilityNotBuilt", en: "This feature is coming soon." },
  },
  [PLAN_LIMIT_REACHED_CODE]: {
    key: "errors.planLimitReached",
    en: "You have reached your plan's limit ({limit}). Change your plan from Subscription to add more.",
    vars: (d) => (Number.isInteger(d?.limit) ? { limit: d.limit } : null),
    bare: { key: "errors.planLimitReachedNoNumber", en: "You have reached your plan's limit. Change your plan from Subscription to add more." },
  },
};

/** A known code (with its structured detail, when it has one) as a sentence; `null` if unknown. */
const knownMessage = (code, detail, t) => {
  const entry = KNOWN_CODE_MESSAGES[code];
  if (!entry) return null;
  const vars = entry.vars ? entry.vars(detail) : null;
  const { key, en } = entry.vars && !vars ? entry.bare : entry;
  if (typeof t === "function") return t(key, vars, en);
  return vars ? Object.keys(vars).reduce((out, k) => out.split(`{${k}}`).join(String(vars[k])), en) : en;
};

/**
 * The localised sentence for a bare code this file knows (one that arrives outside an error
 * envelope, e.g. `GET /saas/check-slug`'s `code`), `null` for any other code.
 */
export const knownCodeMessage = (code, t) => (code ? knownMessage(code, null, t) : null);

/**
 * The machine-stable code of a refusal, whatever envelope it travelled in — `""` when
 * the error carries none.
 *
 * `detail` is read as a string (the shape tenant_middleware and the auth layer send)
 * and as a `{code}` object (the shape the structured refusals use), so ONE reader
 * serves both and a later change of envelope on one path cannot silently stop a
 * classifier from recognising its code.
 *
 * The read below is deliberately written as the bare, fully optional-chained assignment
 * into a local that is then compared: that is the one shape
 * __tests__/apiErrorHygiene.static.test.js recognises as safe, because such a value is
 * never handed to a renderer. Any other spelling is flagged as a raw `detail` render.
 */
export const apiErrorCode = (e) => {
  const detail = e?.response?.data?.detail;
  return (typeof detail === "string" ? detail : detail?.code) || "";
};

/**
 * The practices a `TENANT_SELECTION_REQUIRED` refusal offers, `[]` when it carries none.
 *
 * Lives here, next to `apiErrorCode`, for the same reason: the envelope of a refusal is
 * this file's business, and a consumer that reached into `response.data.detail` itself
 * would be a raw-`detail` read in a component (the shape
 * __tests__/apiErrorHygiene.static.test.js exists to keep out).
 */
export const tenantChoices = (e) => {
  const detail = e?.response?.data?.detail;
  return Array.isArray(detail?.tenants) ? detail.tenants : [];
};

/**
 * The support reference a coded refusal carries (`detail.ref`, e.g. the Stripe request id
 * of a refused billing call), `""` when it carries none. Same reason as `tenantChoices`
 * for living here: the refusal's envelope is this file's business.
 */
export const apiErrorRef = (e) => {
  const detail = e?.response?.data?.detail;
  return typeof detail?.ref === "string" ? detail.ref : "";
};

/**
 * The `failed` list a coded refusal carries (`detail.failed`, e.g. the subscriptions Stripe did
 * not confirm cancelled when a practice deletion is refused), `[]` when it carries none.
 * Same reason as `tenantChoices` for living here: the refusal's envelope is this file's business.
 */
export const apiErrorFailed = (e) => {
  const detail = e?.response?.data?.detail;
  return Array.isArray(detail?.failed) ? detail.failed : [];
};

/**
 * What a `PRACTICE_DELETE_OWED` refusal says is still owed (D7 contract 3.14): `owed` is
 * `[{cents, currency}]` per currency, `payouts` and `clientPayments` (the wire's
 * `client_payments`: client payments not settled yet) are `[{id, status, amount: {cents,
 * currency}}]`; each `[]` when the refusal carries none.
 */
export const apiErrorOwed = (e) => {
  const detail = e?.response?.data?.detail;
  return {
    owed: Array.isArray(detail?.owed) ? detail.owed : [],
    payouts: Array.isArray(detail?.payouts) ? detail.payouts : [],
    clientPayments: Array.isArray(detail?.client_payments) ? detail.client_payments : [],
  };
};

/**
 * Did this request fail because the practice EXISTS but is not being served — as opposed
 * to not existing at all?
 *
 * The distinction is the whole of BUG-041's public half: a suspended practice that tells
 * its own clients "site not found" loses them permanently, whereas an unknown host owes
 * them exactly that message. Both the tenant-host bootstrap (GET /public/site, refused by
 * tenant_middleware) and the platform-host `/t/{slug}` bootstrap (GET
 * /api/saas/tenant/{slug}, refused by saas_routes.get_tenant_public) reject with the same
 * 403, so ONE predicate serves both — tenant/TenantSite.jsx and lib/TenantContext.jsx.
 *
 * `detail` is read as a string (the shape both refusals send) and as a `{code}` object
 * (the shape the structured refusals elsewhere in the API use), so a later change of error
 * envelope degrades to the not-found screen rather than to a wrong claim.
 *
 * The read below is deliberately written as the bare, fully optional-chained assignment
 * into a local that is then compared: that is the one shape
 * __tests__/apiErrorHygiene.static.test.js recognises as safe, because such a value is
 * never handed to a renderer. Any other spelling is flagged as a raw `detail` render.
 */
export const isTenantSuspendedError = (e) =>
  e?.response?.status === 403 && apiErrorCode(e) === TENANT_SUSPENDED_CODE;

/**
 * The localised sentence for a refusal whose code THIS FILE knows (the plan-limit and
 * capability refusals, …), `null` for any other refusal.
 *
 * For modules with their own code vocabulary (admin/crm/api/crmErrors.js): they name
 * their own codes, defer to this for the shared ones, and never fall through to
 * `extractApiError`'s last resort — a JSON dump of an unknown structured `detail`.
 */
export const knownApiErrorMessage = (e, t) => {
  const detail = e?.response?.data?.detail;
  const code = typeof detail === "string" ? detail : detail?.code;
  if (!code) return null;
  return knownMessage(code, detail && typeof detail === "object" ? detail : null, t);
};

export const extractApiError = (e, fallback = "", t) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") {
    const known = knownMessage(d, null, t);
    if (known !== null) return known;
    return d;
  }
  if (Array.isArray(d)) {
    // Pydantic's `msg` is its OWN validation prose, written in English
    // ("Input should be greater than or equal to 0") — a developer diagnostic, never
    // meant for a French or Spanish buyer or practitioner (same rule as
    // checkoutErrorMessage() on the money path). It must never reach this string, in
    // any locale. `loc` (minus the leading "body") is kept: a schema field path is a
    // technical identifier, not natural-language prose.
    //
    // The generic replacement is translated through `t` when the caller has one bound
    // (e.g. `useT("common")`) — extractApiError is a lib function, so it never calls a
    // hook itself. Callers that pass none (most of today's ~40 call sites; none needed
    // editing for this fix) get the English phrase, which is BASE_LANG — never a
    // hardcoded French fallback.
    const genericMsg = typeof t === "function" ? t("errors.invalidFieldValue", null, "Invalid value") : "Invalid value";
    return d
      .map((it) => {
        const field = Array.isArray(it?.loc) ? it.loc.filter((x) => x !== "body").join(".") : "";
        return field ? `${field} : ${genericMsg}` : genericMsg;
      })
      .join(" ; ");
  }
  if (d && typeof d === "object") {
    // Structured API errors carry { code, message } (e.g. CONNECT_NOT_READY). A code this
    // file knows how to NAME comes first — the backend sends the constant precisely so the
    // sentence can be localised here; then the server's own message; only then a JSON dump.
    const known = knownMessage(d.code, d, t);
    if (known !== null) return known;
    if (typeof d.message === "string" && d.message) return d.message;
    try { return JSON.stringify(d); } catch { return fallback; }
  }
  return e?.message || fallback;
};
